import { Permit, PermitCancellationRequestData, PermitTransferRequestData, PermitSuspensionRequestData, PermitTransferCompletionData } from '../types';

// Use the same API_BASE_URL pattern as authService
const API_BASE_URL = '/api/v1';

export interface PermitListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Permit[];
}

export interface CreatePermitRequest {
  permit_type: string;
  work_description: string;
  work_location: string;
  requested_start_time: string;
  requested_end_time: string;
  recipient?: number;
  // Add other fields as needed
  [key: string]: any;
}

export class ApiError extends Error {
  data: any;
  status: number;

  constructor(message: string, data: any, status: number) {
    super(message);
    this.name = 'ApiError';
    this.data = data;
    this.status = status;
  }
}

class PermitService {
  private baseURL: string;
  private csrfToken: string | null = null;

  constructor() {
    this.baseURL = `${API_BASE_URL}/permits`;
    console.log('🔧 PermitService initialized with:', {
      API_BASE_URL,
      baseURL: this.baseURL,
      environment: 'development',
    });
  }

  /**
   * Get CSRF token from backend
   */
  private async getCSRFToken(): Promise<string | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/users/auth/csrf_token/`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        this.csrfToken = data.csrf_token;
        return this.csrfToken;
      }
    } catch (error) {
      console.warn('⚠️ Failed to get CSRF token:', error);
    }
    return null;
  }

  /**
   * Get common headers for authenticated requests
   */
  private async getHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };

    // Try to get CSRF token if we don't have one
    if (!this.csrfToken) {
      await this.getCSRFToken();
    }

    // Add CSRF token to headers if available
    if (this.csrfToken) {
      headers['X-CSRFToken'] = this.csrfToken;
    }

    return headers;
  }

  private async parseError(response: Response) {
    const data = await response.json().catch(async () => {
      const text = await response.text().catch(() => '');
      return { detail: text || 'Request failed' };
    });
    const message = data?.error || data?.detail || `HTTP ${response.status}: Request failed`;
    return { data, message };
  }

  /**
   * Get all permits (with filtering support)
   */
  async getPermits(params?: Record<string, string>): Promise<PermitListResponse> {
    const searchParams = new URLSearchParams(params);
    const url = `${this.baseURL}/permits/${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    
    console.log('📋 Fetching permits:', { url });

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include', // Important for session authentication
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch permits' }));
      throw new Error(errorData.detail || `HTTP ${response.status}: Failed to fetch permits`);
    }

    const data = await response.json();
    
    // Handle both paginated and non-paginated responses
    if (Array.isArray(data)) {
      return {
        count: data.length,
        next: null,
        previous: null,
        results: data,
      };
    }
    
    return data;
  }

  /**
   * Get a specific permit by ID
   */
  async getPermit(id: number): Promise<Permit> {
    const response = await fetch(`${this.baseURL}/permits/${id}/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to fetch permit' }));
      throw new Error(errorData.detail || `HTTP ${response.status}: Failed to fetch permit`);
    }

    return response.json();
  }

  /**
   * Create a new permit
   */
  async createPermit(permitData: CreatePermitRequest): Promise<Permit> {
    console.log('✨ Creating permit:', permitData);

    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseURL}/permits/`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(permitData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to create permit' }));
      console.error('❌ Create permit failed:', errorData);
      throw new Error(errorData.detail || `HTTP ${response.status}: Failed to create permit`);
    }

    const permit = await response.json();
    console.log('✅ Permit created successfully:', permit);
    return permit;
  }

  /**
   * Update an existing permit
   */
  async updatePermit(id: number, permitData: Partial<CreatePermitRequest>): Promise<Permit> {
    console.log('📝 Updating permit:', { id, data: permitData });

    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseURL}/permits/${id}/`, {
      method: 'PATCH',
      headers,
      credentials: 'include',
      body: JSON.stringify(permitData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to update permit' }));
      console.error('❌ Update permit failed:', errorData);
      throw new Error(errorData.detail || `HTTP ${response.status}: Failed to update permit`);
    }

    const permit = await response.json();
    console.log('✅ Permit updated successfully:', permit);
    return permit;
  }

  /**
   * Delete a permit
   */
  async deletePermit(id: number): Promise<void> {
    console.log('🗑️ Deleting permit:', id);

    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseURL}/permits/${id}/`, {
      method: 'DELETE',
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to delete permit' }));
      console.error('❌ Delete permit failed:', errorData);
      throw new Error(errorData.detail || `HTTP ${response.status}: Failed to delete permit`);
    }

    console.log('✅ Permit deleted successfully');
  }

  /**
   * Update permit status
   */
  async updatePermitStatus(id: number, status: string, notes?: string): Promise<Permit> {
    console.log('🔄 Updating permit status:', { id, status, notes });

    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseURL}/permits/${id}/update_status/`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify({ status, notes }),
    });

    if (!response.ok) {
      const { data, message } = await this.parseError(response);
      console.error('❌ Update permit status failed:', data);
      throw new ApiError(message, data, response.status);
    }

    const permit = await response.json();
    console.log('✅ Permit status updated successfully:', permit);
    return permit;
  }

  /**
   * Request permit cancellation with StayLive workflow compliance
   */
  async requestCancellation(id: number, cancellationData: PermitCancellationRequestData): Promise<Permit> {
    console.log('🔄 Requesting permit cancellation:', { id, cancellationData });

    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseURL}/permits/${id}/request_cancellation/`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(cancellationData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to request permit cancellation' }));
      console.error('❌ Request permit cancellation failed:', errorData);
      throw new Error(errorData.detail || `HTTP ${response.status}: Failed to request permit cancellation`);
    }

    const result = await response.json();
    console.log('✅ Permit cancellation requested successfully:', result);
    return result;
  }

  async requestTransfer(id: number, transferData: PermitTransferRequestData): Promise<Permit> {
    console.log('🔄 Requesting permit transfer:', { id, transferData });

    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseURL}/permits/${id}/request_transfer/`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(transferData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to request permit transfer' }));
      console.error('❌ Request permit transfer failed:', errorData);
      throw new Error(errorData.detail || `HTTP ${response.status}: Failed to request permit transfer`);
    }

    const result = await response.json();
    console.log('✅ Permit transfer requested successfully:', result);
    return result;
  }

  async requestSuspension(id: number, suspensionData: PermitSuspensionRequestData): Promise<Permit> {
    console.log('🔄 Requesting permit suspension:', { id, suspensionData });

    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseURL}/permits/${id}/request_suspension/`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(suspensionData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Failed to request permit suspension' }));
      console.error('❌ Request permit suspension failed:', errorData);
      throw new Error(errorData.detail || `HTTP ${response.status}: Failed to request permit suspension`);
    }

    const result = await response.json();
    console.log('✅ Permit suspension requested successfully:', result);
    return result;
  }

  async completeTransfer(id: number, data: PermitTransferCompletionData): Promise<Permit> {
    console.log('🔄 Completing permit transfer:', { id, data });
    const headers = await this.getHeaders();
    const response = await fetch(`${this.baseURL}/permits/${id}/complete_transfer/`, {
      method: 'POST',
      headers,
      credentials: 'include',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const { data, message } = await this.parseError(response);
      console.error('❌ Complete permit transfer failed:', data);
      throw new ApiError(message, data, response.status);
    }
    const result = await response.json();
    console.log('✅ Permit transfer completed successfully:', result);
    return result;
  }

  // Generate standard permit PDF
  async generatePermitPDF(permitId: string): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/permits/${permitId}/generate_pdf/`, {
      method: 'POST',
      headers: await this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new ApiError(
        errorData?.detail || `HTTP error ${response.status}`,
        errorData,
        response.status
      );
    }

    return response.blob();
  }

  // Generate Access Permit specific PDF
  async generateAccessPermitPDF(permitId: string): Promise<Blob> {
    const response = await fetch(`${API_BASE_URL}/permits/${permitId}/generate_access_permit_pdf/`, {
      method: 'POST',
      headers: await this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new ApiError(
        errorData?.detail || `HTTP error ${response.status}`,
        errorData,
        response.status
      );
    }

    return response.blob();
  }

  // Get Access Permit form data with isolation and work party records
  async getAccessPermitData(permitId: string): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/permits/${permitId}/access_permit_form/`, {
      method: 'GET',
      headers: await this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new ApiError(
        errorData?.detail || `HTTP error ${response.status}`,
        errorData,
        response.status
      );
    }

    return response.json();
  }

  // Save Access Permit form data including isolation and work party records
  async saveAccessPermitData(permitId: string, formData: any): Promise<any> {
    const response = await fetch(`${API_BASE_URL}/permits/${permitId}/access_permit_form/`, {
      method: 'POST',
      headers: await this.getHeaders(),
      body: JSON.stringify(formData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new ApiError(
        errorData?.detail || `HTTP error ${response.status}`,
        errorData,
        response.status
      );
    }

    return response.json();
  }

  /**
   * Get next permit sequence number for work control number generation
   */
  async getNextPermitSequence(permitType: string, siteCode: string): Promise<{ next_sequence: number }> {
    const response = await fetch(`${API_BASE_URL}/permits/next-sequence/?permit_type=${permitType}&site_code=${siteCode}`, {
      method: 'GET',
      headers: await this.getHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new ApiError(
        errorData?.detail || `HTTP error ${response.status}`,
        errorData,
        response.status
      );
    }

    return response.json();
  }
}

// Export singleton instance
const permitService = new PermitService();
export default permitService; 